import images from "./image-urls.json";
import details from "./product-details.json";
import mixAImage from "./assets/mix-a.jpg";

export type Form = "Oral tablets" | "Injection" | "Blend injection";

export interface GenderedContent {
  general?: string | null;
  male?: string | null;
  female?: string | null;
}

export interface ProductDetails {
  pharmaceuticalName?: string | null;
  chemicalName?: string | null;
  molecularFormula?: string | null;
  molecularWeight?: string | null;
  proprietaryName?: string | null;
  dosageForm?: string | null;
  composition?: string | null;
  classification?: string | null;
  mechanism?: string | null;
  pharmacokinetics?: string | null;
  indications?: GenderedContent | null;
  adverseReactions?: GenderedContent | null;
  contraindications?: string | null;
  warning?: string | null;
  specialPrecautions?: string | null;
  drugInteractions?: string | null;
  drivingEffect?: string | null;
  overdose?: string | null;
  presentation?: string | null;
  dosage?: GenderedContent | null;
  storage?: string | null;
}

export interface Product {
  slug: string;
  proprietaryName: string;
  pharmaceuticalName: string;
  category: "Oral" | "Injectable" | "Blend";
  form: Form;
  strength: string;
  molecularFormula: string;
  molecularWeight: string;
  classification: string;
  tagline: string;
  shortDescription: string;
  mechanism: string;
  indications: string[];
  presentation: string;
  storage: string;
  image: string | null;
  gradient: string;
  details?: ProductDetails;
}

const imgs = images as Record<string, string | null>;
const localImages: Record<string, string> = {
  "MIX A": mixAImage,
};

const img = (key: string) => localImages[key] ?? imgs[key] ?? null;

export const products: Product[] = [
  {
    slug: "test-e",
    proprietaryName: "TEST E",
    pharmaceuticalName: "Testosterone Enanthate",
    category: "Injectable",
    form: "Injection",
    strength: "200 mg / 1 ml",
    molecularFormula: "C₂₆H₄₀O₃",
    molecularWeight: "400.6 g/mol",
    classification: "Androgenic / Anabolic steroid",
    tagline: "The cornerstone of sustained androgen therapy.",
    shortDescription:
      "Long-acting testosterone ester with a 4.5-day half-life, engineered for steady plasma levels and reduced dosing frequency.",
    mechanism:
      "Testosterone Enanthate binds to androgen receptors throughout the body after slow intramuscular release, driving protein synthesis, nitrogen retention, and the full spectrum of endogenous testosterone activity.",
    indications: [
      "Male hypogonadism & testosterone replacement therapy",
      "Delayed puberty under medical supervision",
      "Muscle wasting syndromes (HIV/AIDS-associated cachexia)",
      "Adjunct therapy in select breast carcinoma cases",
    ],
    presentation: "10 ampoules of 1 ml (200 mg/ml) in a blister pack.",
    storage: "Store below 30 °C. Protect from light. Do not refrigerate.",
    image: img("TEST E"),
    gradient: "from-amber-400/20 to-yellow-600/10",
  },
  {
    slug: "test-c",
    proprietaryName: "TEST C",
    pharmaceuticalName: "Testosterone Cypionate",
    category: "Injectable",
    form: "Injection",
    strength: "200 mg / 1 ml",
    molecularFormula: "C₂₇H₄₀O₃",
    molecularWeight: "412.6 g/mol",
    classification: "Androgenic / Anabolic steroid",
    tagline: "Extended-release testosterone for steady-state androgen support.",
    shortDescription:
      "Cypionate ester prolongs testosterone activity with delayed onset and peak plasma concentrations within 1–2 days after IM injection.",
    mechanism:
      "Binds to androgen receptors after enzymatic ester cleavage, influencing gene transcription and protein synthesis across muscle, bone, and reproductive tissues.",
    indications: [
      "Treatment of hypogonadism in males",
      "Delayed puberty stimulation",
      "Support in androgen deficiency syndromes",
      "Adjunct in bone density management",
    ],
    presentation: "10 ampoules of 1 ml (200 mg/ml) in a blister pack.",
    storage: "Store below 30 °C. Protect from light. Do not refrigerate.",
    image: img("TEST C"),
    gradient: "from-amber-300/20 to-orange-500/10",
  },
  {
    slug: "test-p",
    proprietaryName: "TEST P",
    pharmaceuticalName: "Testosterone Propionate",
    category: "Injectable",
    form: "Injection",
    strength: "100 mg / 1 ml",
    molecularFormula: "C₂₂H₃₂O₃",
    molecularWeight: "344.5 g/mol",
    classification: "Androgenic / Anabolic steroid",
    tagline: "Short-ester testosterone for rapid onset and precise control.",
    shortDescription:
      "Rapidly absorbed propionate ester with quick clearance — ideal when fast onset and short duration of action are desired.",
    mechanism:
      "Propionic-acid ester is rapidly cleaved upon injection, releasing free testosterone to bind androgen receptors and trigger androgen-dependent gene expression.",
    indications: [
      "Short-term testosterone replacement",
      "Delayed puberty in adolescent males",
      "Androgen deficiency syndromes",
      "Muscle-wasting disorders",
    ],
    presentation: "10 ampoules of 1 ml (100 mg/ml) in a blister pack.",
    storage: "Store below 30 °C. Protect from light. Do not refrigerate.",
    image: img("TEST P"),
    gradient: "from-orange-400/20 to-red-500/10",
  },
  {
    slug: "sustanon",
    proprietaryName: "SUSTANON",
    pharmaceuticalName: "Testosterone Propionate + Phenylpropionate + Isocaproate + Decanoate",
    category: "Blend",
    form: "Blend injection",
    strength: "250 mg / 1 ml",
    molecularFormula: "Blend",
    molecularWeight: "344.5 · 420.6 · 386.6 · 442.7 g/mol",
    classification: "Androgenic / Anabolic steroid",
    tagline: "Four esters. One elegant pharmacokinetic curve.",
    shortDescription:
      "A quadruple-ester testosterone blend engineered to deliver immediate onset from propionate through prolonged release with decanoate.",
    mechanism:
      "Each ester cleaves at its own rate, providing fast-acting to long-acting testosterone in a single injection. All liberate free testosterone for androgen-receptor binding.",
    indications: [
      "Hormone replacement therapy for hypogonadism",
      "Delayed puberty under medical supervision",
      "Select cases of male breast cancer therapy",
    ],
    presentation: "10 ampoules of 1 ml (250 mg/ml) in a blister pack.",
    storage: "Store below 30 °C. Protect from light. Do not refrigerate.",
    image: img("SUSTANON"),
    gradient: "from-yellow-400/20 to-amber-600/10",
  },
  {
    slug: "bold-u",
    proprietaryName: "BOLD U",
    pharmaceuticalName: "Boldenone Undecylenate",
    category: "Injectable",
    form: "Injection",
    strength: "300 mg / 1 ml",
    molecularFormula: "C₃₀H₄₄O₃",
    molecularWeight: "452.7 g/mol",
    classification: "Androgenic / Anabolic steroid",
    tagline: "Long-ester testosterone derivative with sustained anabolic reach.",
    shortDescription:
      "Boldenone esterified for weeks of stable plasma levels — an anabolic with minimal androgenic noise.",
    mechanism:
      "Slow-release ester cleaves via esterases. Boldenone then promotes nitrogen retention, erythropoiesis, and gradual lean-mass development.",
    indications: [
      "Supportive anabolic therapy",
      "Endurance & red-cell-mediated performance support",
      "Off-label lean muscle development",
    ],
    presentation: "10 ampoules of 1 ml (300 mg/ml) in a blister pack.",
    storage: "Store below 30 °C. Protect from light. Do not refrigerate.",
    image: img("BOLD U"),
    gradient: "from-amber-500/20 to-yellow-700/10",
  },
  {
    slug: "mast-e",
    proprietaryName: "MAST E",
    pharmaceuticalName: "Drostanolone Enanthate",
    category: "Injectable",
    form: "Injection",
    strength: "200 mg / 1 ml",
    molecularFormula: "C₂₇H₄₄O₃",
    molecularWeight: "416.6 g/mol",
    classification: "Androgenic / Anabolic steroid",
    tagline: "Refined DHT derivative for definition and hardness.",
    shortDescription:
      "Long-ester drostanolone for gradual release — non-aromatizing with pronounced anti-estrogenic character.",
    mechanism:
      "DHT-derived AAS binding directly to androgen receptors; does not convert to estrogen, lending a dry, hardened aesthetic profile.",
    indications: [
      "Advanced breast carcinoma (historical indication)",
      "Addressing testosterone deficiency as adjunct",
      "Preservation of lean mass in cutting protocols",
    ],
    presentation: "10 ampoules of 1 ml (200 mg/ml) — 2 blisters per carton.",
    storage: "Store below 30 °C. Protect from light. Do not refrigerate.",
    image: img("MAST E"),
    gradient: "from-slate-400/20 to-zinc-600/10",
  },
  {
    slug: "mast-p",
    proprietaryName: "MAST P",
    pharmaceuticalName: "Drostanolone Propionate",
    category: "Injectable",
    form: "Injection",
    strength: "100 mg / 1 ml",
    molecularFormula: "C₂₃H₃₆O₃",
    molecularWeight: "360.5 g/mol",
    classification: "Androgenic / Anabolic steroid",
    tagline: "Fast-acting masteron for precision cutting phases.",
    shortDescription:
      "Short-ester drostanolone for rapid onset and fine-grained serum control.",
    mechanism:
      "Derived from dihydrotestosterone, binds androgen receptors with non-aromatizing anabolic activity and a stronger androgenic signature.",
    indications: [
      "Postmenopausal estrogen-receptor-positive tumors",
      "Supplementing testosterone in hypogonadal males",
    ],
    presentation: "10 ampoules of 1 ml (100 mg/ml) in a blister pack.",
    storage: "Store below 30 °C. Protect from light. Do not refrigerate.",
    image: img("MAST P"),
    gradient: "from-slate-300/20 to-gray-500/10",
  },
  {
    slug: "tren-a",
    proprietaryName: "TREN A",
    pharmaceuticalName: "Trenbolone Acetate",
    category: "Injectable",
    form: "Injection",
    strength: "100 mg / 1 ml",
    molecularFormula: "C₂₀H₂₄O₃",
    molecularWeight: "312.4 g/mol",
    classification: "Androgenic / Anabolic steroid",
    tagline: "The apex anabolic — intense, direct, fast.",
    shortDescription:
      "Short-ester trenbolone for rapid onset. 1–2 day half-life demands precise, frequent dosing under supervision.",
    mechanism:
      "High-affinity androgen-receptor binding with potent anti-glucocorticoid action — increases protein synthesis, red cell output, and lean mass retention under caloric restriction.",
    indications: [
      "Specialist anabolic support under close monitoring",
      "Preservation of lean mass in catabolic states",
    ],
    presentation: "10 ampoules of 1 ml (100 mg/ml) — 2 blisters per carton.",
    storage: "Store below 30 °C. Protect from light. Do not refrigerate.",
    image: img("TREN A"),
    gradient: "from-red-500/20 to-rose-700/10",
  },
  {
    slug: "tren-e",
    proprietaryName: "TREN E",
    pharmaceuticalName: "Trenbolone Enanthate",
    category: "Injectable",
    form: "Injection",
    strength: "200 mg / 1 ml",
    molecularFormula: "C₂₅H₃₄O₃",
    molecularWeight: "382.5 g/mol",
    classification: "Androgenic / Anabolic steroid",
    tagline: "Long-ester trenbolone for sustained anabolic dominance.",
    shortDescription:
      "Enanthate-ester trenbolone for prolonged serum activity and reduced dosing frequency.",
    mechanism:
      "Slow ester cleavage from oil depot releases trenbolone over days; receptor binding elevates protein synthesis while suppressing catabolic pathways.",
    indications: [
      "Specialist anabolic support",
      "Catabolic state recovery (veterinary origin)",
    ],
    presentation: "10 ampoules of 1 ml (200 mg/ml) — 2 blisters per carton.",
    storage: "Store below 30 °C. Protect from light. Do not refrigerate.",
    image: img("TREN E"),
    gradient: "from-rose-500/20 to-red-700/10",
  },
  {
    slug: "tren-h",
    proprietaryName: "TREN HEX",
    pharmaceuticalName: "Trenbolone Hexahydrobenzylcarbonate",
    category: "Injectable",
    form: "Injection",
    strength: "100 mg / 1 ml",
    molecularFormula: "C₂₇H₃₄O₃",
    molecularWeight: "406.56 g/mol",
    classification: "Androgenic / Anabolic steroid",
    tagline: "Parabolan — the longest-acting trenbolone ester.",
    shortDescription:
      "Hexahydrobenzylcarbonate ester delivers the slowest trenbolone release — peak levels accumulate gradually for infrequent dosing.",
    mechanism:
      "Trenbolone bound to a long carbonate ester; post-hydrolysis, powerfully binds androgen receptors, inhibits glucocorticoid activity, stimulates erythropoiesis.",
    indications: [
      "Specialist anabolic support under supervision",
      "Lean-mass preservation in catabolic states",
    ],
    presentation: "10 ampoules of 1 ml (100 mg/ml) — 2 blisters per carton.",
    storage: "Store below 30 °C. Protect from light. Do not refrigerate.",
    image: img("TREN H"),
    gradient: "from-red-600/20 to-amber-800/10",
  },
  {
    slug: "nandrolone-d",
    proprietaryName: "NANDROLONE D",
    pharmaceuticalName: "Nandrolone Decanoate",
    category: "Injectable",
    form: "Injection",
    strength: "200 mg / 1 ml",
    molecularFormula: "C₂₈H₄₄O₃",
    molecularWeight: "428.6 g/mol",
    classification: "Androgenic / Anabolic steroid",
    tagline: "Deca — the joint-friendly long-ester anabolic.",
    shortDescription:
      "Decanoate-esterified nandrolone provides 6–12 day half-life and sustained anabolic action with mild androgenic profile.",
    mechanism:
      "Binds androgen receptor in muscle, bone, and bone marrow — classically used to stimulate erythropoiesis, preserve lean mass, and support bone density.",
    indications: [
      "Hypogonadism replacement therapy",
      "Chronic anemia in renal insufficiency",
      "Muscle-wasting syndromes (HIV/AIDS, dystrophy)",
      "Osteoporosis with androgen deficiency",
    ],
    presentation: "10 ampoules of 1 ml (200 mg/ml) in a blister pack.",
    storage: "Store below 30 °C. Protect from light. Do not refrigerate.",
    image: img("NANDROLONE D"),
    gradient: "from-blue-400/20 to-indigo-600/10",
  },
  {
    slug: "npp",
    proprietaryName: "NPP",
    pharmaceuticalName: "Nandrolone Phenylpropionate",
    category: "Injectable",
    form: "Injection",
    strength: "100 mg / 1 ml",
    molecularFormula: "C₂₇H₃₄O₃",
    molecularWeight: "406.6 g/mol",
    classification: "Androgenic / Anabolic steroid",
    tagline: "Short-ester nandrolone for controllable anabolic support.",
    shortDescription:
      "Phenylpropionate ester offers ~4-day half-life — faster onset than decanoate with more responsive dosing windows.",
    mechanism:
      "Androgen-receptor agonism in muscle, bone, adipose tissue; supports erythropoiesis and lean-mass development.",
    indications: [
      "Hypogonadism therapy",
      "Cachexia in chronic illness",
      "Chronic anemia support",
    ],
    presentation: "10 ampoules of 1 ml (100 mg/ml) in a blister pack.",
    storage: "Store below 30 °C. Protect from light. Do not refrigerate.",
    image: img("NPP"),
    gradient: "from-indigo-400/20 to-blue-600/10",
  },
  {
    slug: "primo",
    proprietaryName: "PRIMO",
    pharmaceuticalName: "Methenolone Enanthate",
    category: "Injectable",
    form: "Injection",
    strength: "100 mg / 1 ml",
    molecularFormula: "C₂₇H₄₂O₃",
    molecularWeight: "414.6 g/mol",
    classification: "Androgenic / Anabolic steroid",
    tagline: "Primobolan — the refined choice for mild, quality lean-mass support.",
    shortDescription:
      "Long-acting methenolone with 5–7 day half-life — non-aromatizing, gentle androgenic pressure, favored for cutting phases.",
    mechanism:
      "Androgen-receptor binding with protein-synthesis activation and nitrogen retention; does not aromatize to estrogen.",
    indications: [
      "Androgen deficiency in males & adjunct in females",
      "Muscle-wasting in chronic illness / cachexia",
      "Adjunctive in delayed puberty (rare)",
    ],
    presentation: "10 ampoules of 1 ml (100 mg/ml) in a blister pack.",
    storage: "Store below 30 °C. Protect from light. Do not refrigerate.",
    image: img("PRIMO"),
    gradient: "from-emerald-400/20 to-teal-600/10",
  },
  {
    slug: "mix-a",
    proprietaryName: "MIX A",
    pharmaceuticalName: "Trenbolone Acetate + Drostanolone Propionate + Testosterone Propionate",
    category: "Blend",
    form: "Blend injection",
    strength: "300 mg / 1 ml",
    molecularFormula: "Blend",
    molecularWeight: "312.4 · 360.5 · 344.5 g/mol",
    classification: "Androgenic / Anabolic steroid",
    tagline: "The definition blend — three short esters, one injection.",
    shortDescription:
      "Trenbolone Acetate 100 mg + Drostanolone Propionate 100 mg + Testosterone Propionate 100 mg — all short esters, all synchronized kinetics.",
    mechanism:
      "Each constituent binds androgen receptors with complementary profiles: Tren for anabolic potency, Masteron for hardness, Test Prop as substrate baseline.",
    indications: [
      "Cutting-phase androgen support (specialist use)",
      "Hypogonadism adjunct under supervision",
    ],
    presentation: "10 ampoules of 1 ml (300 mg/ml) — 2 blisters per carton.",
    storage: "Store below 30 °C. Protect from light. Do not refrigerate.",
    image: img("MIX A"),
    gradient: "from-rose-500/20 to-amber-700/10",
  },
  {
    slug: "mix-b",
    proprietaryName: "MIX B",
    pharmaceuticalName: "Testosterone Enanthate + Trenbolone Enanthate + Boldenone Undecylenate",
    category: "Blend",
    form: "Blend injection",
    strength: "400 mg / 1 ml",
    molecularFormula: "Blend",
    molecularWeight: "400.6 · 382.5 · 452.7 g/mol",
    classification: "Androgenic / Anabolic steroid",
    tagline: "The long-ester tri-blend for sustained mass cycles.",
    shortDescription:
      "Testosterone Enanthate 100 mg + Trenbolone Enanthate 100 mg + Boldenone Undecylenate 200 mg — harmonized long-ester kinetics in one vial.",
    mechanism:
      "Test provides systemic androgen baseline; Tren delivers potent anabolic pressure; Boldenone adds endurance and erythropoietic support.",
    indications: [
      "Long-cycle androgen / anabolic support under supervision",
      "Specialist lean mass development",
    ],
    presentation: "10 ampoules of 1 ml (400 mg/ml) in a blister pack.",
    storage: "Store below 30 °C. Protect from light. Do not refrigerate.",
    image: img("MIX B"),
    gradient: "from-amber-500/20 to-red-700/10",
  },
  {
    slug: "anavar",
    proprietaryName: "ANAVAR",
    pharmaceuticalName: "Oxandrolone",
    category: "Oral",
    form: "Oral tablets",
    strength: "20 mg tablet",
    molecularFormula: "C₁₉H₃₀O₃",
    molecularWeight: "306.44 g/mol",
    classification: "Anabolic androgenic steroid",
    tagline: "The gentlest oral — quality lean-mass preservation.",
    shortDescription:
      "17α-alkylated DHT-derivative; non-aromatizing and well-tolerated relative to other orals.",
    mechanism:
      "Androgen-receptor binding drives protein synthesis, nitrogen retention, and preservation of lean tissue without estrogen conversion.",
    indications: [
      "Weight regain after surgery, trauma, or chronic infection",
      "Muscle-wasting conditions",
      "Osteoporosis",
      "Recovery after prolonged corticosteroid therapy",
    ],
    presentation: "50 tablets of 20 mg in blister pack or bottle.",
    storage: "Store below 25 °C. Protect from light and moisture.",
    image: img("ANAVAR"),
    gradient: "from-teal-400/20 to-cyan-600/10",
  },
  {
    slug: "anadrol",
    proprietaryName: "ANADROL",
    pharmaceuticalName: "Oxymetholone",
    category: "Oral",
    form: "Oral tablets",
    strength: "50 mg tablet",
    molecularFormula: "C₂₁H₃₂O₃",
    molecularWeight: "332.48 g/mol",
    classification: "Anabolic androgenic steroid",
    tagline: "Maximum oral anabolism — decisive, powerful, monitored.",
    shortDescription:
      "Potent 17α-alkylated oral AAS with strong erythropoietic and protein-synthesis effects.",
    mechanism:
      "Androgen-receptor agonism plus aggressive stimulation of red-blood-cell production, protein synthesis, and nitrogen retention.",
    indications: [
      "Anemia from deficient red-cell production",
      "Aplastic anemia",
      "Myelofibrosis",
      "Hypoplastic anemia",
    ],
    presentation: "50 tablets of 50 mg in blister pack or bottle.",
    storage: "Store below 25 °C. Protect from light and moisture.",
    image: img("ANADROL"),
    gradient: "from-red-500/20 to-orange-700/10",
  },
  {
    slug: "dianabol",
    proprietaryName: "DIANABOL",
    pharmaceuticalName: "Methandienone",
    category: "Oral",
    form: "Oral tablets",
    strength: "20 mg tablet",
    molecularFormula: "C₂₀H₂₈O₂",
    molecularWeight: "300.44 g/mol",
    classification: "Anabolic androgenic steroid",
    tagline: "The original oral — the foundation of bulk protocols.",
    shortDescription:
      "Historic 17α-alkylated testosterone derivative; fast onset, powerful anabolic activity.",
    mechanism:
      "Binds androgen receptors in skeletal muscle and bone; rapid protein synthesis and nitrogen retention with mild aromatization.",
    indications: [
      "Historic use in protein catabolism and lean-mass loss",
      "Adjunct in muscle-wasting protocols (limited modern use)",
    ],
    presentation: "50 tablets of 20 mg in blister pack or bottle.",
    storage: "Store below 25 °C. Protect from light and moisture.",
    image: img("DIANABOL"),
    gradient: "from-pink-500/20 to-red-600/10",
  },
  {
    slug: "winstrol",
    proprietaryName: "WINSTROL",
    pharmaceuticalName: "Stanozolol",
    category: "Oral",
    form: "Oral tablets",
    strength: "20 mg tablet",
    molecularFormula: "C₂₁H₃₂N₂O",
    molecularWeight: "328.49 g/mol",
    classification: "Anabolic androgenic steroid",
    tagline: "The definition oral — crisp, dry, iconic.",
    shortDescription:
      "DHT-derived, non-aromatizing 17α-alkylated oral — favored when water retention is undesirable.",
    mechanism:
      "Activates androgen receptors to drive protein synthesis and nitrogen retention, with no estrogen conversion.",
    indications: [
      "Hereditary angioedema",
      "Muscle wasting",
      "Select anemia-related conditions",
    ],
    presentation: "50 tablets of 20 mg in blister pack or bottle.",
    storage: "Store below 25 °C. Protect from light and moisture.",
    image: img("WINSTROL"),
    gradient: "from-slate-300/20 to-blue-500/10",
  },
  {
    slug: "turinabol",
    proprietaryName: "TURINABOL",
    pharmaceuticalName: "Chlorodehydromethyltestosterone",
    category: "Oral",
    form: "Oral tablets",
    strength: "20 mg tablet",
    molecularFormula: "C₂₀H₂₇ClO₂",
    molecularWeight: "334.88 g/mol",
    classification: "Anabolic androgenic steroid",
    tagline: "The clean oral — measured gains, controlled profile.",
    shortDescription:
      "Chlorinated derivative of methandienone; the chlorine substitution eliminates aromatization and reduces androgenic signal.",
    mechanism:
      "17α-alkylated oral with steady androgen-receptor agonism and no estrogen conversion — favored for smooth, lean progression.",
    indications: [
      "Muscle-wasting conditions",
      "Recovery from illness or injury",
      "Preservation of lean body mass",
    ],
    presentation: "50 tablets of 20 mg in blister pack or bottle.",
    storage: "Store below 25 °C. Protect from light and moisture.",
    image: img("TURINABOL"),
    gradient: "from-lime-400/20 to-emerald-600/10",
  },
  {
    slug: "halotestin",
    proprietaryName: "HALOTESTIN",
    pharmaceuticalName: "Fluoxymesterone",
    category: "Oral",
    form: "Oral tablets",
    strength: "10 mg tablet",
    molecularFormula: "C₂₀H₂₉FO₃",
    molecularWeight: "336.44 g/mol",
    classification: "Anabolic androgenic steroid",
    tagline: "Pure androgenic potency — specialist use only.",
    shortDescription:
      "Fluorinated 17α-alkylated AAS with pronounced androgenic activity and minimal aromatization.",
    mechanism:
      "Potent androgen-receptor agonist; does not convert to estrogen. Effects are skewed toward strength, aggression, and red-cell increase.",
    indications: [
      "Male hypogonadism (historical use)",
      "Delayed puberty in males",
      "Certain breast carcinoma cases in women",
    ],
    presentation: "50 tablets of 10 mg in blister pack or bottle.",
    storage: "Store below 25 °C. Protect from light and moisture.",
    image: img("HALOTESTIN"),
    gradient: "from-yellow-400/20 to-red-600/10",
  },
  {
    slug: "proviron",
    proprietaryName: "PROVIRON",
    pharmaceuticalName: "Mesterolone",
    category: "Oral",
    form: "Oral tablets",
    strength: "25 mg tablet",
    molecularFormula: "C₂₀H₃₂O₂",
    molecularWeight: "304.47 g/mol",
    classification: "Androgenic steroid",
    tagline: "The androgen modulator — SHBG-reducer & free-test enhancer.",
    shortDescription:
      "Orally active DHT-derivative; non-17α-alkylated, non-aromatizing, minimal anabolic activity.",
    mechanism:
      "Binds androgen receptor and lowers sex-hormone-binding globulin (SHBG), increasing free testosterone. Does not aromatize.",
    indications: [
      "Male hypogonadism",
      "Infertility linked to low androgen levels",
      "Reduced libido from androgen deficiency",
    ],
    presentation: "50 tablets of 25 mg in blister pack or bottle.",
    storage: "Store below 25 °C. Protect from light and moisture.",
    image: img("PROVIRON"),
    gradient: "from-amber-300/20 to-yellow-500/10",
  },
];

const detailsMap = details as Record<string, ProductDetails>;
for (const p of products) {
  const d = detailsMap[p.proprietaryName];
  if (d) p.details = d;
}

export const productsBySlug: Record<string, Product> = Object.fromEntries(
  products.map((p) => [p.slug, p])
);

export const categories = ["Injectable", "Blend", "Oral"] as const;
